import { BoundaryEventType } from '../../../shared/entities/boundary-event/boundary-event.common'
import {
  ActivityNode,
  EventNodes,
  GatewayNodes,
  NodeType,
} from '../../../shared/entities/node.types'

export const nodeTypes = [
  {
    icon: 'task',
    type: ActivityNode.TASK,
  },
  {
    icon: 'user-task',
    type: ActivityNode.USER_TASK,
  },
  {
    icon: 'subprocess-run',
    type: ActivityNode.SUBPROCESS_RUN,
  },
  {
    icon: 'gateway',
    type: GatewayNodes.GATEWAY_EXCLUSIVE,
  },
  {
    icon: 'gateway-parallel',
    type: GatewayNodes.GATEWAY_PARALLEL,
  },
  {
    icon: 'send-task',
    type: ActivityNode.MESSAGE_EVENT_SEND,
  },
  {
    icon: 'receive-task',
    type: ActivityNode.MESSAGE_EVENT_RECEIVE,
  },
  {
    icon: 'start-milestone',
    type: EventNodes.START_MILESTONE,
  },
  {
    icon: 'end-milestone',
    type: EventNodes.END_MILESTONE,
  },
  {
    icon: 'start-event',
    type: EventNodes.START_EVENT,
  },
  {
    icon: 'end-event',
    type: EventNodes.END_EVENT,
  },
  {
    icon: 'message-throw-intermediate-event',
    type: EventNodes.INTERMEDIATE_MESSAGE_THROW_EVENT,
  },
  {
    icon: 'message-catch-intermediate-event',
    type: EventNodes.INTERMEDIATE_MESSAGE_CATCH_EVENT,
  },
  {
    icon: 'message-catch-start-event',
    type: EventNodes.START_MESSAGE_CATCH_EVENT,
  },
  {
    icon: 'boundary-catch-non-interrupting-event',
    type: BoundaryEventType.MESSAGE_CATCH_NON_INTERRUPTING,
  },
  {
    icon: 'boundary-timer-interrupting-event',
    type: EventNodes.INTERMEDIATE_TIMER_EVENT,
  },
  {
    icon: 'boundary-timer-start-event',
    type: EventNodes.START_TIMER_EVENT,
  },
  {
    icon: 'boundary-timer-non-interrupting-event',
    type: BoundaryEventType.TIMER_NON_INTERRUPTING,
  },
  {
    icon: 'boundary-error-catch-interrupting-event',
    type: BoundaryEventType.ERROR_CATCH_INTERRUPTING,
  },
]
export interface NodeTypeGrouped {
  icon: string
  title?: string
  type?: NodeType | BoundaryEventType
  subnodes?: any[]
  description?: string
}

export const nodeTypesGrouped: {
  title?: string
  type?: NodeType
  subnodes?: NodeTypeGrouped[]
  showSubnodes?: boolean
}[] = [
  {
    title: 'Activities',
    subnodes: [
      {
        icon: 'user-task',
        type: ActivityNode.USER_TASK,
        title: 'User Task',
        description:
          'Represents an interaction with the user and it is used to display a piece of UI.',
      },
      {
        icon: 'task',
        type: ActivityNode.TASK,
        title: 'Service Task',
        description:
          'Refers to a task that utilises various services to accomplish a particular task.',
      },
      {
        icon: 'subprocess-run',
        type: ActivityNode.SUBPROCESS_EMBED,
        title: 'Start Embedded Subprocess',
        description:
          'Initiates subprocesses within a process, allowing for encapsulated functionality.',
      },
      {
        icon: 'call-activity',
        type: ActivityNode.SUBPROCESS_RUN,
        title: 'Call Activity',
        description: 'Provides advanced options for starting subprocesses.',
      },
      {
        icon: 'send-task',
        type: ActivityNode.MESSAGE_EVENT_SEND,
        title: 'Send Message Task',
        description: 'Used to configure messages that should be sent to external systems.',
      },
      {
        icon: 'receive-task',
        type: ActivityNode.MESSAGE_EVENT_RECEIVE,
        title: 'Receive Message Task',
        description: 'Used when we need to wait for a reply from an external system.',
      },
    ],
  },
  {
    title: 'Start Events',
    subnodes: [
      {
        icon: 'start-group',
        type: EventNodes.START_EVENT,
        title: 'Start Process',
        description: 'Represents the beginning of a process.',
      },
      {
        icon: 'message-catch-start-event',
        type: EventNodes.START_MESSAGE_CATCH_EVENT,
        title: 'Message Start Event',
        description:
          'Represents the starting point for a process instance based on the receipt of a message.',
      },
      {
        icon: 'boundary-timer-start-event',
        type: EventNodes.START_TIMER_EVENT,
        title: 'Timer Start Event',
        description: 'Initiates a process instance based on a specified time or schedule.',
      },
    ],
  },
  {
    title: 'Intermediate Events',
    subnodes: [
      {
        icon: 'message-throw-intermediate-event',
        type: EventNodes.INTERMEDIATE_MESSAGE_THROW_EVENT,
        title: 'Message Throw Event',
        description: 'Throws a message and continues with the process flow.',
      },
      {
        icon: 'message-catch-intermediate-event',
        type: EventNodes.INTERMEDIATE_MESSAGE_CATCH_EVENT,
        title: 'Message Catch Event - Interrupting',
        description:
          'Triggered at any time while the associated task is being performed, interrupting the task.',
      },
      {
        icon: 'boundary-catch-non-interrupting-event',
        type: BoundaryEventType.MESSAGE_CATCH_NON_INTERRUPTING,
        title: 'Message Catch Event - Non-Interrupting',
        description: 'Triggered at any time while the associated task is being performed.',
      },
      {
        icon: 'boundary-timer-interrupting-event',
        type: EventNodes.INTERMEDIATE_TIMER_EVENT,
        title: 'Timer Event - Interrupting',
        description:
          'Used to pause the process execution until a specified time duration or date is reached.',
      },
      {
        icon: 'boundary-timer-non-interrupting-event',
        type: BoundaryEventType.TIMER_NON_INTERRUPTING,
        title: 'Timer Event - Non-Interrupting',
        description:
          'Initiates a process instance at a scheduled time, either interrupting or non-interrupting.',
      },
      {
        icon: 'boundary-error-catch-interrupting-event',
        type: BoundaryEventType.ERROR_CATCH_INTERRUPTING,
        title: 'Error Event - Interrupting',
        description:
          'Expands the capabilities of process modelling and error handling within BPMN processing.',
      },
    ],
  },
  {
    title: 'End Events',
    subnodes: [
      {
        icon: 'end-event',
        type: EventNodes.END_EVENT,
        title: 'End Process',
        description: 'Used to mark where the process finishes.',
      },
    ],
  },
  {
    title: 'Gateways',
    subnodes: [
      {
        icon: 'gateway',
        type: GatewayNodes.GATEWAY_EXCLUSIVE,
        title: 'Exclusive Gateway',
        description:
          'Represents a decision point in the process where one outgoing path is selected.',
      },
      {
        icon: 'gateway-parallel',
        type: GatewayNodes.GATEWAY_PARALLEL,
        title: 'Parallel Gateway',
        description:
          'Creates a parallel section within the process for tasks that can run independently.',
      },
    ],
  },
]

/**
 * Kebab-cases a dictionary `title` into a test-id token. Shared so the palette, the
 * canvas New Node panel and their tests all derive the same token from one place.
 * `title` is optional on NodeTypeGrouped, so an absent one slugs to '' rather than throwing.
 * The output is a test-id contract: changing this renames test ids already in use.
 */
export const titleSlug = (title?: string): string =>
  (title ?? '').toLowerCase().replace(/\s+/g, '-')

export const NODE_HOVER_DEBOUNCE_TIME = 1000
